import { z } from 'zod'
import { Snapshot } from './generatie.js'
import { InstantieId, SectionId, VersiemomentId } from './id.js'
import { PersonForExternal } from './identity.js'
import { Laag } from './laag.js'
import { Moment, Person } from './moment.js'

/**
 * Een versiemoment: een automatisch vastgelegde stand van één sectiedocument binnen één generatie
 * (ontwerp 6.9, R-97, R-188).
 *
 * **Waarvoor.** De geschiedenisweergave van de renderer (`collab/history.ts`) legt een Yjs-snapshot
 * naast het levende document: "wie schreef wat" sinds dat punt, en "wat is er veranderd sinds deze
 * stand". Tot nu toe was het enige ijkpunt binnen een generatie haar nulsnapshot. Een versiemoment
 * geeft tussenpunten, zoals de automatische checkpoints van de CitatenPOC (bijlage C.1).
 *
 * **Vastgelegd door de pod, met gezag.** In de POC zette elke browser zelf een checkpoint en hielp
 * een willekeurige vertraging om twee medewerkers niet tegelijk te laten schrijven (bijlage C.4). Hier
 * neemt de pod ze, en alleen de houder van de schrijflease van die generatie: de opslag weigert een
 * versiemoment zonder geldige lease, net als een schrijfactie op het document zelf (3.2).
 *
 * **Alleen te renderen binnen zijn generatie.** Een snapshot betekent alleen iets tegen het document
 * waaruit hij is genomen (6.9, 4.6), dus hij draagt het nummer van die generatie mee, zoals
 * `SectionSnapshot` op een revisie.
 *
 * **Geen revisie.** Een revisie is de bevroren stand bij een overdracht (R-95) en is append-only; een
 * versiemoment is een hulpmiddel bij het samen schrijven, en de opslag houdt er per sectie en
 * generatie alleen de nieuwste twintig van.
 */

export const Versiemoment = z.strictObject({
  id: VersiemomentId,
  instantieId: InstantieId,
  laag: Laag,
  sectieId: SectionId,
  /** De generatie van het sectiedocument waaruit de snapshot komt (4.6). */
  generatie: z.number().int().positive(),
  /**
   * Wanneer de pod dit moment vastlegde. Alle versiemomenten van één ronde van de pod dragen
   * hetzelfde moment, zodat de lijst ze per formulier kan groeperen.
   */
  moment: Moment,
  /** De Yjs-snapshot, base64 in JSON. De opslag bewaart hem binair en interpreteert hem nooit. */
  snapshot: Snapshot,
  /**
   * Wie er sinds het vorige versiemoment van deze sectie in deze generatie iets veranderde: de
   * personen achter de Yjs-clients wier stand is opgelopen, uit de auteursmap van het document
   * (`authorOf`), aangevuld met wie in die periode alleen iets verwijderde. Uit het token, nooit uit
   * de client (R-154), op volgorde en zonder dubbelen.
   */
  auteurs: z.array(Person),
})
export type Versiemoment = z.infer<typeof Versiemoment>

/**
 * Wat de pod de opslag stuurt. Het pad noemt de instantie, laag, sectie en generatie.
 *
 * `houder` en `epoch` zijn de schrijflease, met dezelfde betekenis als bij het wegschrijven van het
 * document (3.2, slot 3): een versiemoment van een pod die de lease niet heeft wordt geweigerd.
 */
export const VersiemomentRequest = z.strictObject({
  moment: Moment,
  snapshot: Snapshot,
  /** Minstens één: zonder wijziging van een persoon legt de pod geen versiemoment vast. */
  auteurs: z.array(Person).min(1),
  houder: z.string().min(1),
  epoch: z.number().int().positive().optional(),
})
export type VersiemomentRequest = z.infer<typeof VersiemomentRequest>

/**
 * De soort van een regel in de lijst van de pod.
 *
 * `begin` is het begin van de generatie, met haar nulsnapshot en haar openingsmoment; `automatisch`
 * is een versiemoment dat de pod vastlegde.
 */
export const VersiemomentKind = z.enum(['begin', 'automatisch'])
export type VersiemomentKind = z.infer<typeof VersiemomentKind>

/** Eén sectie in een regel van de lijst: de snapshot met zijn generatie, en wie er schreef. */
export const VersiemomentSection = z.strictObject({
  laag: Laag,
  sectieId: SectionId,
  generatie: z.number().int().positive(),
  snapshot: Snapshot,
  /** Een persoon, of voor de externe kant het merkteken van NWO waar een persoon van de interne kant staat (R-66a). */
  auteurs: z.array(PersonForExternal),
})
export type VersiemomentSection = z.infer<typeof VersiemomentSection>

/**
 * Eén regel in de lijst: alle secties die op hetzelfde moment een versiemoment kregen, of op
 * hetzelfde moment aan hun generatie begonnen.
 *
 * `auteurs` is de vereniging over de secties, zonder dubbelen en met het merkteken hooguit één keer.
 */
export const FormVersiemoment = z.strictObject({
  moment: Moment,
  soort: VersiemomentKind,
  auteurs: z.array(PersonForExternal),
  secties: z.array(VersiemomentSection).min(1),
})
export type FormVersiemoment = z.infer<typeof FormVersiemoment>

/**
 * Het antwoord van `GET /versiemomenten?instantie=` op de pod: per regel een moment, nieuwste eerst.
 *
 * Alleen de huidige, levende generatie van elke sectie die deze lezer mag lezen (R-179): een snapshot
 * uit een gearchiveerde generatie past niet op het document dat de browser heeft.
 */
export const VersiemomentList = z.strictObject({
  instantie: InstantieId,
  versiemomenten: z.array(FormVersiemoment),
})
export type VersiemomentList = z.infer<typeof VersiemomentList>
